import { ProgressOverviewButton } from "@components/progress-overview/progress-overview-button";
import { ProgressOverviewSection } from "@components/progress-overview/progress-overview-section";
import { InfoIcon, neutral, yellow } from "@flat-ui";
import { Popover, Stack, Typography } from "@mui/material";
import { useAppSelector } from "@store/store-helper";
import {
  hasIncompleteUploadTasksSelector,
  hasInProgressUploadTasksSelector,
} from "@store/upload-tasks/upload-tasks-selector";
import {
  DEFAULT_TEXT_FONT_SIZE,
  DEFAULT_TITLE_FONT_SIZE,
} from "@styles/common-styles";
import { ProgressOverviewEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/** Renders a menu to track the upload progress */
export function ProgressOverviewMenu(): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const hasUploadTask = useAppSelector(hasIncompleteUploadTasksSelector);
  const hasInProgressUploadTask = useAppSelector(
    hasInProgressUploadTasksSelector
  );

  const [isOpen, setIsOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!hasUploadTask) {
      setIsOpen(false);
    }
  }, [hasUploadTask]);

  /** Opens the progress overview menu */
  const handleClick = useCallback<MouseEventHandler>(
    (ev) => {
      if (!isOpen) {
        trackEvent({ name: ProgressOverviewEvents.openProgressOverview });
        setIsOpen(true);
        ev.stopPropagation();
      }
    },
    [isOpen, trackEvent]
  );

  return (
    <>
      <ProgressOverviewButton
        ref={button}
        onClick={handleClick}
        isLoading={hasInProgressUploadTask}
        disabled={!hasUploadTask}
      />
      <Popover
        open={isOpen}
        anchorEl={button.current}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Stack
          component="div"
          sx={{
            width: "100%",
            maxWidth: "400px",
            maxHeight: "550px",
            borderRadius: "5px",
            p: 0,
            my: 4,
            overflow: "hidden",
          }}
          data-testid="progress-overview-menu"
        >
          <Typography
            sx={{
              fontSize: DEFAULT_TITLE_FONT_SIZE,
              fontWeight: 600,
              mx: "32px",
            }}
          >
            Uploads
          </Typography>

          <ProgressOverviewSection />

          <Stack
            sx={{
              flexDirection: "row",
              pt: 2,
              mx: 4,
              alignItems: "flex-start",
            }}
          >
            <InfoIcon sx={{ color: yellow[500], pt: 0.3, height: "16px" }} />
            <Typography
              sx={{
                color: neutral[700],
                fontSize: DEFAULT_TEXT_FONT_SIZE,
              }}
            >
              Progress will be lost only if you close the browser while
              uploading your files.
            </Typography>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
